import React, { useState } from 'react';
import { toolboxService } from '../services/api';

const ConversationMessagesImport = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  const handleFileChange = (event) => {
    setError('');
    setResult(null);
    const selected = event.target.files?.[0];
    setFile(selected || null);
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a CSV file to import.');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await toolboxService.importConversationMessagesCsv(file);
      if (response.success) {
        setResult(response);
      } else {
        setError(response.error || 'Failed to import conversation messages.');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred while importing conversation messages.');
    } finally {
      setLoading(false);
    }
  };

  if (!isLocalhost) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Conversation Messages CSV Import</h1>
          <p className="text-gray-600">
            This tool is only available when accessing the dashboard from <strong>localhost</strong> for local testing.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Conversation Messages CSV Import</h1>
        <p className="text-gray-600 mb-6">
          Upload a <code className="bg-gray-100 px-1 py-0.5 rounded border border-gray-200">conversation_message.csv</code> file
          (e.g. from <code className="bg-gray-100 px-1 py-0.5 rounded border border-gray-200">tests/conversation_message.csv</code>)
          to import records into the local database. This is intended for local testing only.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CSV File</label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            type="button"
            onClick={handleImport}
            disabled={loading || !file}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Importing...' : 'Import Conversation Messages'}
          </button>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {result && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded space-y-2">
              <p className="font-semibold">Import Complete</p>
              <p>
                Imported <span className="font-mono">{result.imported}</span> messages.
              </p>
              {Array.isArray(result.errors) && result.errors.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm font-medium">
                    {result.errors.length} row(s) failed to import
                  </summary>
                  <ul className="mt-2 text-sm list-disc list-inside space-y-1 max-h-40 overflow-y-auto">
                    {result.errors.map((err, index) => (
                      <li key={`${err.row}-${index}`}>
                        Row {err.row}{err.id ? ` (id: ${err.id})` : ''}: {err.error}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationMessagesImport;

